import React from 'react';
import { FileText, Briefcase, PenLine, Send, MessageCircle, CheckCircle2 } from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────
// JourneyStrip · B-EXP-6.14 (Commit 4)
//
// Six-step diagetic timeline. Derives each step's state entirely from
// real case/brief data — no new lifecycle_stage field, no synthetic
// status names. The stripe is the customer/staff-facing timeline; the
// actual write-side status vocabulary lives in the status dropdown.
//
//   • Intake          — case created (event.created_at)
//   • Workspace       — case claimed (state.claimed_at or claim.worker_id)
//   • Brief in prog.  — draft has ≥ 1 recommendation
//   • Delivered       — publishStatus.published (sealed manual brief)
//   • Follow-up       — case status == 'followup_needed'
//   • Closed          — case status == 'closed'
//
// State machine (per step) — {done | current | future}:
//   The step with the highest ordinal that has any "activity signal"
//   becomes 'current'; earlier steps become 'done'; later steps stay
//   'future'. Closed always short-circuits to Closed=current.
//   Follow-up promotes to 'current' when its status is active even if
//   nothing later has happened; it can also be 'done' when we've moved
//   past it (i.e. Closed).
// ──────────────────────────────────────────────────────────────────────

const STEPS = [
  { key: 'intake',    label: 'Intake',            icon: FileText },
  { key: 'workspace', label: 'Workspace',         icon: Briefcase },
  { key: 'brief',     label: 'Brief in progress', icon: PenLine },
  { key: 'delivered', label: 'Delivered',         icon: Send },
  { key: 'followup',  label: 'Follow-up',         icon: MessageCircle },
  { key: 'closed',    label: 'Closed',            icon: CheckCircle2 },
];

const friendlyShort = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (_) {
    return null;
  }
};

// Extract a robust recommendation count from an event.brief_draft
// payload. Tolerates dict-of-arrays, missing keys, and legacy shapes.
const countRecs = (briefDraft) => {
  if (!briefDraft || typeof briefDraft !== 'object') return 0;
  const recs = briefDraft.recommendations || briefDraft.recs || {};
  if (Array.isArray(recs)) return recs.length;
  let total = 0;
  for (const slot of Object.values(recs)) {
    if (Array.isArray(slot)) total += slot.length;
  }
  return total;
};

export default function JourneyStrip({
  createdAt,
  claimedAt,
  status,
  briefDraft,
  publishStatus,
  publishedAt,
  closedAt,
}) {
  const isClosed = status === 'closed';
  const isFollowup = status === 'followup_needed';
  const recCount = countRecs(briefDraft);
  const isPublished = !!(publishStatus && publishStatus.published);
  const hasClaimed = !!claimedAt;

  // Determine the "current" step — the highest-ordinal step whose
  // activity signal is present. Closed short-circuits.
  const currentKey = (() => {
    if (isClosed) return 'closed';
    if (isFollowup) return 'followup';
    if (isPublished) return 'delivered';
    if (recCount > 0) return 'brief';
    if (hasClaimed) return 'workspace';
    return 'intake';
  })();

  const currentIdx = STEPS.findIndex((s) => s.key === currentKey);

  const stepState = (key, idx) => {
    if (isClosed) {
      // In closed cases: everything before "closed" is done,
      // "closed" itself is current.
      if (key === 'closed') return 'current';
      // Follow-up only counts as "done" if it was activated (best-
      // effort — we don't have historical audit here; keep it lit for
      // continuity so the strip visually resolves).
      return 'done';
    }
    if (idx < currentIdx) return 'done';
    if (idx === currentIdx) return 'current';
    return 'future';
  };

  const subLabel = (key, state) => {
    switch (key) {
      case 'intake':
        return friendlyShort(createdAt) || '\u2014';
      case 'workspace':
        if (state === 'done') return hasClaimed ? (friendlyShort(claimedAt) || 'completed') : 'completed';
        if (hasClaimed) return 'you are here';
        return '\u2014';
      case 'brief':
        if (recCount === 0) {
          if (state === 'done') return 'completed';
          if (state === 'current') return 'in progress';
          return '\u2014';
        }
        return recCount === 1 ? '1 recommendation' : `${recCount} recommendations`;
      case 'delivered':
        if (isPublished) {
          const stamp = friendlyShort(publishStatus?.published_at || publishedAt);
          const rev = publishStatus?.revision;
          if (rev && stamp) return `v${rev} · ${stamp}`;
          if (stamp) return stamp;
          if (rev) return `v${rev}`;
          return 'sent';
        }
        return '\u2014';
      case 'followup':
        if (isFollowup) return 'you are here';
        if (state === 'done') return 'completed';
        return '\u2014';
      case 'closed':
        if (isClosed) {
          const stamp = friendlyShort(closedAt);
          return stamp || 'closed';
        }
        return '\u2014';
      default:
        return '\u2014';
    }
  };

  return (
    <div
      className="border-b border-[#EFE3CE] bg-[#FFF7EE] overflow-hidden"
      data-testid="case-journey-strip"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 overflow-x-auto">
        <ol className="flex items-center gap-2 sm:gap-3 w-max sm:w-auto">
          {STEPS.map((step, idx) => {
            const state = stepState(step.key, idx);
            const Icon = step.icon;
            const dotClass =
              state === 'done'
                ? 'bg-[#5B47E8] text-white border-[#5B47E8]'
                : state === 'current'
                  ? 'text-white border-[#5B47E8] shadow-[0_0_0_4px_rgba(91,71,232,0.15)]'
                  : 'bg-[#FBF3E4] text-[#B4A47F] border-[#EAD9B6]';
            const currentBgStyle = state === 'current' ? { backgroundColor: '#5B47E8' } : undefined;
            const labelClass =
              state === 'done'
                ? 'text-gray-500'
                : state === 'current'
                  ? 'text-gray-900 font-semibold'
                  : 'text-[#A99367]';
            const connectorClass =
              state === 'done' || state === 'current' ? 'bg-[#5B47E8]/70' : 'bg-[#EAD9B6]';
            return (
              <React.Fragment key={step.key}>
                <li
                  className="flex flex-col items-center text-center w-24 sm:w-28 shrink-0"
                  data-testid={`case-journey-step-${step.key}`}
                  data-state={state}
                >
                  <span
                    className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${dotClass}`}
                    style={currentBgStyle}
                    aria-hidden
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className={`mt-2 text-[11.5px] leading-tight ${labelClass}`}>{step.label}</span>
                  <span className="mt-0.5 text-[10px] text-[#A99367] leading-tight">
                    {subLabel(step.key, state)}
                  </span>
                </li>
                {idx < STEPS.length - 1 ? (
                  <li
                    className={`flex-1 h-[2px] mt-[-24px] min-w-[24px] sm:min-w-[32px] rounded ${connectorClass}`}
                    aria-hidden
                  />
                ) : null}
              </React.Fragment>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
